import { html, css, unsafeCSS } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { localized } from "@lit/localize";

import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-card";
import "../../elements/web-kiosk-home-link";
import type { FeedbackContent } from "../../types/content";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-feedback": WebKioskFeedbackElement;
  }
}

@localized()
@customElement("web-kiosk-feedback")
export class WebKioskFeedbackElement extends WebKioskPageElement {
  @state() content: FeedbackContent | undefined = undefined;

  render(): TemplateResult {
    return html` <section class="page-body">
      <div class="body-inner">
        <web-kiosk-home-link
          link=${this.createRelativeLink("home")}
        ></web-kiosk-home-link>

        <section class="body-content">
          <section class="feedback-text">
            <h1>${this.content?.heading}</h1>

            <p>${this.content?.description}</p>
          </section>
        </section>

        <article class="qr-code">
          <h2>${this.content?.surveyHeading}</h2>
          <section class="survey">
            <img
              src=${this.content?.surveyQrCodeLink}
              alt=${this.content?.surveyQrCodeLinkAlt}
            />
            <section class="phone">
              <h3 class="iphone">${this.content?.phoneHeading}</h3>
              <p class="iphone-p">${this.content?.phoneDescription}</p>
              <h3>${this.content?.oldPhoneHeading}</h3>
              <p>${this.content?.oldPhoneDescription}</p>
            </section>
          </section>
        </article>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
    }

    .page-body {
      height: 100%;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/feedback/peopleTable.svg") top right 10% no-repeat,
        url("assets/feedback/plantRight.svg") bottom right 58% no-repeat,
        url("assets/feedback/window.svg") top 10% right 5% no-repeat,
        url("assets/feedback/feedbackPageVector.svg") bottom right 40% no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
      background-size: 45%, 8%, 15%, 35%;
    }

    .body-inner {
      padding: 48px 56px;
    }

    .body-content {
      display: flex;
      gap: 210px;
    }

    .feedback-text,
    .qr-code {
      width: 38%;
      flex: 0 38%;
      margin-left: 8px;
    }

    .feedback-text h1 {
      font-size: 72px;
      font-weight: 700;
      line-height: 82px;
      margin: 48px 0 40px;
    }

    a,
    .feedback-text :is(h1),
    h2 h3 h4 {
      color: var(--web-kiosk-text-color, hotpink);
    }

    .feedback-text p {
      font-size: 26px;
      font-weight: 400px;
      line-height: 40px;
      margin-bottom: 88px;
      padding-right: 12px;
    }

    .qr-code h2 {
      margin-bottom: 24px;
      font-size: 28px;
      font-weight: 700px;
      line-height: 48px;
    }

    .survey {
      display: flex;
      align-items: flex-start;
    }

    .phone {
      margin: 0 24px;
    }

    .phone h3 {
      font-size: 22px;
      font-weight: 700px;
      line-height: 32px;
      margin-bottom: 0;
    }

    .phone p {
      margin-top: 0;
      font-size: 20px;
      font-weight: 400px;
      line-height: 28px;
    }

    .iphone-p {
      margin-bottom: 16px;
    }

    .iphone {
      margin-top: 0;
    }
  `;
}
